import { Box, Button, Container, Flex, Icon, Link, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { MdMarkEmailRead } from 'react-icons/md';
import { Link as RRLink } from 'react-router-dom';
import * as Yup from 'yup';

import Loader from '../../components/Loader';
import SimpleFooter from '../../components/SimpleFooter';
import Input from '../../components/formComponents/Input';
import { LDFlags } from '../../constants/flags';
import { CREATE_ACCOUNT } from '../../constants/urls';
import {
  useCheckExistingAccountByEmailLazyQuery,
  useHasAnyDealLazyQuery,
} from '../../gql/generated/graphql';
import { useFlag } from '../../hooks';
import { RedirectType, startPasswordlessEmailLink } from '../../services/auth0';
import { formatDuration } from '../../utils/helpers';
import { emailValidationRequired, validateEmail } from '../../utils/validation/email';
import Subtitle from './components/Subtitle';
import Title from './components/Title';
import { useOneClickLoginAttempts } from './useOneClickLoginAttempts';

const STEP_EMAIL = 'email';
const STEP_SENT = 'sent';
const LINK_REQUEST_ERROR = 'Something went wrong. Please try again.';

type Props = {
  setShowOneClickLogin: (a: boolean) => void;
  passedEmail: string;
  initialError?: string;
};

const OneClickLogin = ({ setShowOneClickLogin, passedEmail, initialError }: Props) => {
  const hasRun = useRef(false);
  const passwordlessDashboardAccessEnabled = useFlag(LDFlags.PASSWORDLESS_DASHBOARD_ACCESS);

  const { cooldownRemaining, recordAttempt } = useOneClickLoginAttempts();

  const [checkExistingAccountByEmail] = useCheckExistingAccountByEmailLazyQuery({
    fetchPolicy: 'network-only',
    context: { isErrorHandled: true },
  });

  const [checkHasAnyDeal] = useHasAnyDealLazyQuery({
    fetchPolicy: 'network-only',
    context: { isErrorHandled: true },
  });

  const [step, setStep] = useState<keyof typeof STEPS>(STEP_EMAIL);
  const [error, setError] = useState('');
  const [emailSentTo, setEmailSentTo] = useState('');

  const handleSubmit = async (values: { email: string }) => {
    const { email } = values;

    setError('');
    setEmailSentTo(email);

    const waitRemaining = cooldownRemaining(email);

    if (waitRemaining > 0) {
      setError(`Please wait ${formatDuration(waitRemaining)} before requesting another link.`);

      return;
    }

    const [existingAccount, anyDeal] = await Promise.all([
      checkExistingAccountByEmail({ variables: { email } }),
      passwordlessDashboardAccessEnabled ? checkHasAnyDeal({ variables: { email } }) : undefined,
    ]);

    if (existingAccount.error || anyDeal?.error) {
      setError(LINK_REQUEST_ERROR);
      return;
    }

    const shouldSendLink =
      !!existingAccount.data?.checkExistingAccountByEmail || !!anyDeal?.data?.hasAnyDeal;

    if (shouldSendLink) {
      try {
        await startPasswordlessEmailLink({
          redirectType: RedirectType.ACCOUNT_LOGIN,
          email,
        });
      } catch {
        setError(LINK_REQUEST_ERROR);
        return;
      }
    }

    recordAttempt(email);
    setStep(STEP_SENT);
  };

  useEffect(() => {
    if (passedEmail && validateEmail(passedEmail) && !hasRun.current) {
      handleSubmit({ email: passedEmail });
      hasRun.current = true;
    }
  }, [passedEmail]);

  const STEPS = {
    [STEP_EMAIL]: {
      title: passwordlessDashboardAccessEnabled ? 'Welcome Back to Lease End' : 'One-Time Link',
      subtitle: passwordlessDashboardAccessEnabled
        ? ''
        : 'We’ll send you a link to quickly log you in',
      content: (
        <Formik
          enableReinitialize
          initialValues={{ email: passedEmail }}
          validationSchema={Yup.object({
            email: emailValidationRequired,
          })}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {({ errors, isSubmitting }) =>
            !error && isSubmitting ? (
              <Loader />
            ) : (
              <Form>
                <Flex flexDirection="column" bg="white" p={8} rounded="lg" alignItems="center">
                  <Input label="Email Address" name="email" placeholder="Email" autoFocus />
                  {error && !errors.email && (
                    <Text mt={1} mx={1.5} fontSize="sm" color="leaseEndRed">
                      {error}
                    </Text>
                  )}
                  <Button w="100%" mt={6} mb={4} type="submit">
                    {passwordlessDashboardAccessEnabled
                      ? 'SIGN IN WITH ONE-TIME LINK'
                      : 'EMAIL MY LINK'}
                  </Button>
                  <Box textAlign="center">
                    <Button
                      variant="link"
                      onClick={() => setShowOneClickLogin(false)}
                      fontWeight={500}
                    >
                      {passwordlessDashboardAccessEnabled
                        ? 'Use password instead'
                        : 'Back to login'}
                    </Button>
                  </Box>
                  {passwordlessDashboardAccessEnabled && (
                    <Box textAlign="center" mt={4}>
                      Don't have an account?{' '}
                      <Link as={RRLink} to={CREATE_ACCOUNT}>
                        Click to get started!
                      </Link>
                    </Box>
                  )}
                </Flex>
                <SimpleFooter />
              </Form>
            )
          }
        </Formik>
      ),
    },
    [STEP_SENT]: {
      title: passwordlessDashboardAccessEnabled ? 'Welcome Back to Lease End' : 'Check your email',
      subtitle: '',
      content: (
        <>
          <Flex flexDirection="column" bg="white" p={8} rounded="lg" alignItems="center">
            {passwordlessDashboardAccessEnabled && <Subtitle>Check your email</Subtitle>}
            <Text textAlign="center" color="leaseEndBlue" fontSize="md" mb={4}>
              {passwordlessDashboardAccessEnabled
                ? `If you have an account with us, we've sent a one-time login link to ${emailSentTo}. Click the link in your email to sign in securely.`
                : `If the email ${emailSentTo} is associated with an account, you will receive a login link via email. This link will expire in 10 minutes`}
            </Text>
            <Icon as={MdMarkEmailRead} w={16} h={16} mb={4} color="taupeGray" />
            {!passwordlessDashboardAccessEnabled && (
              <Text color="taupeGray" fontSize="sm" textAlign="center" mb={4}>
                If you don't see the email in your inbox, check your spam folder. Don't have an
                account with us yet? Click{' '}
                <Link as={RRLink} to={CREATE_ACCOUNT}>
                  here
                </Link>{' '}
                to create one now!
              </Text>
            )}
            {passwordlessDashboardAccessEnabled ? (
              <Button
                variant="secondary"
                w="100%"
                mb={4}
                type="button"
                onClick={() => setStep(STEP_EMAIL)}
              >
                Go Back
              </Button>
            ) : (
              <Button variant="primary" w="100%" mb={4} onClick={() => setShowOneClickLogin(false)}>
                OK
              </Button>
            )}
            {passwordlessDashboardAccessEnabled ? (
              <Flex color="taupeGray" flexDir="column" alignItems="center" w="full">
                <Text>Didn't receive the email?</Text>
                <Flex alignItems="center" justifyContent="center">
                  <Link onClick={() => setStep(STEP_EMAIL)}>Resend link</Link>
                  <Text mx={4}>&#11825;</Text>
                  <Button
                    variant="link"
                    onClick={() => setShowOneClickLogin(false)}
                    fontWeight={500}
                  >
                    Use password instead
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Link onClick={() => setStep(STEP_EMAIL)}>Resend email</Link>
            )}
          </Flex>
          <SimpleFooter />
        </>
      ),
    },
  };

  return (
    <Container pt={10}>
      <Title>{STEPS[step].title}</Title>
      {initialError && (
        <Text fontSize="sm" color="leaseEndRed" textAlign="center" mb={4} maxW="400px" mx="auto">
          {initialError}
        </Text>
      )}
      {STEPS[step].subtitle && <Subtitle>{STEPS[step].subtitle}</Subtitle>}

      <Box maxW="400px" mx="auto">
        {STEPS[step].content}
      </Box>
    </Container>
  );
};

export default OneClickLogin;
